import React from 'react';
import { Text, StatusChip, Button } from '@fragments';
import Screen from '@components/Screen';
import Parser from '@helpers/parser';
import {
    AccountBalanceRounded,
    BlockRounded,
    InfoOutlined,
    ErrorOutlineRounded,
    PersonRounded,
} from '@mui/icons-material';

import ContestationAlert from './components/ContestationAlert';
import EventTimeline from './components/EventTimeline';
import DetailsSidebar from './components/DetailsSidebar';
import { CHARGEBACK_STATUS_MAP } from '../../tabs/Chargebacks/enums/chargebackStatuses';

import './styles.scss';

function ChargebackStatusChip({ status }) {
    const config = CHARGEBACK_STATUS_MAP[status];
    if (!config) return <StatusChip.Default text={status} />;

    const variants = {
        success: StatusChip.Success,
        error: StatusChip.Error,
        alert: StatusChip.Alert,
        info: StatusChip.Info,
        default: StatusChip.Default,
    };

    const Component = variants[config.chipVariant] || StatusChip.Default;
    return <Component text={config.label} />;
}

function getDaysUntilDeadline(deadline) {
    if (!deadline) return null;
    const diff = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
}

export default function ChargebackDetailsView({
    chargeback,
    loading,
    error,
    navigate,
    chargebackId,
    onDispute,
}) {
    const breadcrumb = [
        { title: 'Banking', url: 'banking', iconLeft: <AccountBalanceRounded /> },
        { title: 'Chargebacks', url: 'banking/chargebacks' },
        { title: loading ? '•••' : `${chargeback?.id || chargebackId}` },
    ];

    if (loading) {
        return (
            <Screen loading title={breadcrumb}>
                <div className="chargeback-details chargeback-details--loading" />
            </Screen>
        );
    }

    if (error || !chargeback) {
        return (
            <Screen title={breadcrumb}>
                <div className="chargeback-details chargeback-details--error">
                    <div className="chargeback-details__error-state">
                        <ErrorOutlineRounded style={{ fontSize: 40, opacity: 0.4 }} />
                        <Text.h4 contrast={5}>{error || 'Contestação não encontrada'}</Text.h4>
                        <Button
                            text="Voltar para chargebacks"
                            color="white"
                            onClick={() => navigate('/banking/chargebacks')}
                        />
                    </div>
                </div>
            </Screen>
        );
    }

    const daysUntilDeadline = getDaysUntilDeadline(chargeback.timestamps?.deadline);
    const showDeadlineBadge = daysUntilDeadline !== null && daysUntilDeadline > 0 && (chargeback.status === 'requested' || chargeback.status === 'waiting_documents');
    const deadlineIsUrgent = daysUntilDeadline !== null && daysUntilDeadline <= 5 && daysUntilDeadline > 0;

    return (
        <Screen title={breadcrumb}>
            <div className="chargeback-details screen-body">
                {/* Page header */}
                <div className="chargeback-details__header">
                    <div className="chargeback-details__header-left  mt-3">
                        <div className="chargeback-details__amount-row">
                            <Text.h2 weight={400}>
                                R$ {Parser.price.brl(chargeback.amount)} BRL
                            </Text.h2>
                            {showDeadlineBadge && (
                                <span className={`chargeback-details__deadline-badge${deadlineIsUrgent ? ' chargeback-details__deadline-badge--urgent' : ''}`}>
                                    <InfoOutlined style={{ fontSize: 13 }} />
                                    {daysUntilDeadline} {daysUntilDeadline === 1 ? 'dia' : 'dias'} restantes para envio
                                </span>
                            )}
                            <ChargebackStatusChip status={chargeback.status} />
                        </div>

                        {chargeback.user && (
                            <Text.span contrast={6} weight={400} left={<PersonRounded />}>
                                Cliente:{' '}
                                <Text.span
                                    weight={400}
                                    contrast={6}
                                    className="chargeback-details__user-link"
                                    onClick={() => navigate(`/user/${chargeback.user.id}`)}
                                    role="button"
                                >
                                    {chargeback.user.username}
                                </Text.span>
                            </Text.span>
                        )}
                    </div>
                </div>

                {/* Two-column layout */}
                <div className="chargeback-details__layout">
                    <div className="chargeback-details__main">
                        <ContestationAlert
                            reason={chargeback.reason}
                            status={chargeback.status}
                            onAccept={() => { }}
                            onDispute={onDispute}
                        />
                        <div className="px-1 mt-2">
                            <EventTimeline events={chargeback.events || []} />
                        </div>
                    </div>

                    <aside className="chargeback-details__sidebar">
                        <DetailsSidebar chargeback={chargeback} />
                    </aside>
                </div>
            </div>
        </Screen>
    );
}
